import { useState } from "react";
import { Box, Typography, Divider, Grid, Input } from "@mui/joy";
import { Timeline as SharedTimeline, Alert, Button } from "../../../../../../shared";
import { IProjectList } from "../../../../../../interfaces";
import {
    CalendarCheck,
    FileCheck,
    HardHat,
    Package,
    ClipboardCheck,
    Flag,
    Home,
    Settings,
    X,
    Check,
} from "lucide-react";
import { useEventHandler } from "../../hooks";

const getMilestones = (details: IProjectList) => {
    const getTimelineInfo = (key: string) => {
        const timeline = details.ProjectTimelines?.find((t: any) => t.DateType === key);
        return {
            targetDate: timeline?.TargetDate || null,
            actualDate: timeline?.ActualDate || null,
        };
    };

    const rawMilestones = [
        {
            id: "milestone-start",
            type: "milestone" as const,
            icon: <Home size={16} />,
            title: "Project Start",
            date: details.StartDate || details.CreatedAt,
            targetDate: details.StartDate || details.CreatedAt,
            details: "Project successfully initialized in the system.",
        },
        {
            id: "milestone-estimate",
            type: "milestone" as const,
            icon: <ClipboardCheck size={16} />,
            title: "Estimate Provided",
            ...getTimelineInfo("ESTIMATE"),
            date:
                getTimelineInfo("ESTIMATE").actualDate ||
                getTimelineInfo("ESTIMATE").targetDate ||
                details.EstimateDate ||
                "",
            details: "Detailed estimate sent to customer.",
        },
        {
            id: "milestone-inspection",
            type: "milestone" as const,
            icon: <CalendarCheck size={16} />,
            title: "Inspection",
            ...getTimelineInfo("INSPECTION"),
            date:
                getTimelineInfo("INSPECTION").actualDate ||
                getTimelineInfo("INSPECTION").targetDate ||
                details.InspectionDate ||
                "",
            details: "Roofing inspection scheduled or completed.",
        },
        {
            id: "milestone-permit",
            type: "milestone" as const,
            icon: <FileCheck size={16} />,
            title: "Permit Submission",
            ...getTimelineInfo("PERMIT_SUBMISSION"),
            date:
                getTimelineInfo("PERMIT_SUBMISSION").actualDate ||
                getTimelineInfo("PERMIT_SUBMISSION").targetDate ||
                details.PermitSubmissionDate ||
                "",
            details: "Permits were submitted to local authorities.",
        },
        {
            id: "milestone-materials",
            type: "milestone" as const,
            icon: <Package size={16} />,
            title: "Materials Purchased",
            ...getTimelineInfo("MATERIAL_PURCHASE"),
            date:
                getTimelineInfo("MATERIAL_PURCHASE").actualDate ||
                getTimelineInfo("MATERIAL_PURCHASE").targetDate ||
                details.MaterialPurchaseDate ||
                "",
            details: "Required roofing materials ordered and secured.",
        },
        {
            id: "milestone-jobstart",
            type: "milestone" as const,
            icon: <HardHat size={16} />,
            title: "Job Started",
            ...getTimelineInfo("JOB_START"),
            date:
                getTimelineInfo("JOB_START").actualDate ||
                getTimelineInfo("JOB_START").targetDate ||
                details.JobStartDate ||
                "",
            details: "On-site operations began on the property.",
        },
        {
            id: "milestone-completion",
            type: "milestone" as const,
            icon: <Flag size={16} />,
            title: "Project Completion",
            ...getTimelineInfo("COMPLETION"),
            date:
                getTimelineInfo("COMPLETION").actualDate ||
                getTimelineInfo("COMPLETION").targetDate ||
                details.CompletionDate ||
                details.ActualEndDate ||
                "",
            details: "Project reached final completion and sign-off.",
        },
    ];

    return rawMilestones
        .filter((m) => !!m.date)
        .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime())
        .map((m) => ({ ...m, user: { name: "System", avatarUrl: "" } })) as any[];
};

export function ProjectTimeline({ details }: { details: IProjectList | null }) {
    const [isEditing, setIsEditing] = useState(false);

    const {
        startDate,
        setStartDate,
        inspectionDate,
        setInspectionDate,
        estimateDate,
        setEstimateDate,
        permitSubmissionDate,
        setPermitSubmissionDate,
        materialPurchaseDate,
        setMaterialPurchaseDate,
        jobStartDate,
        setJobStartDate,
        completionDate,
        setCompletionDate,
        estimatedEndDate,
        setEstimatedEndDate,
        handleUpdate,
        saving,
        success,
        error,
        hasChanges,
    } = useEventHandler(details);

    if (!details) return null;

    const milestones = getMilestones(details);

    const onUpdate = async () => {
        await handleUpdate();
        setIsEditing(false);
    };

    return (
        <Box sx={{ flex: 1, px: 0.5 }}>
            <Box mb={2} mt={1} display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography level="title-md">
                        {isEditing ? "Project Milestones" : "Project Timeline"}
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                        {isEditing
                            ? "Update key dates to track this project's journey."
                            : "Chronological journey of key project phases."}
                    </Typography>
                </Box>
                {!isEditing ? (
                    <Button
                        variant="soft"
                        size="sm"
                        color="neutral"
                        startDecorator={<Settings size={16} />}
                        onClick={() => setIsEditing(true)}
                    >
                        Manage Dates
                    </Button>
                ) : (
                    <Box display="flex" gap={1}>
                        <Button
                            variant="plain"
                            size="sm"
                            color="neutral"
                            startDecorator={<X size={16} />}
                            onClick={() => setIsEditing(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            size="sm"
                            color="primary"
                            startDecorator={<Check size={16} />}
                            onClick={onUpdate}
                            disabled={!hasChanges || saving}
                            loading={saving}
                        >
                            Save Milestones
                        </Button>
                    </Box>
                )}
            </Box>

            {error && <Alert errorMessage={error} sx={{ mb: 2 }} />}
            {success && <Alert successMessage={success} sx={{ mb: 2 }} />}

            <Divider sx={{ mb: 3 }} />

            <Box px={1}>
                {isEditing ? (
                    <Box mt={1}>
                        <Typography
                            level="body-xs"
                            sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                color: "primary.plainColor",
                                textTransform: "uppercase",
                            }}
                        >
                            Project Initiation
                        </Typography>
                        <Grid container spacing={2} mb={3}>
                            <MilestoneField label="Start Date" value={startDate} onChange={setStartDate} />
                        </Grid>

                        <Typography
                            level="body-xs"
                            sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                color: "primary.plainColor",
                                textTransform: "uppercase",
                            }}
                        >
                            Pre-Construction
                        </Typography>
                        <Grid container spacing={2} mb={3}>
                            <MilestoneField label="Estimate Date" value={estimateDate} onChange={setEstimateDate} />
                            <MilestoneField
                                label="Inspection Date"
                                value={inspectionDate}
                                onChange={setInspectionDate}
                            />
                            <MilestoneField
                                label="Permit Submission"
                                value={permitSubmissionDate}
                                onChange={setPermitSubmissionDate}
                            />
                        </Grid>

                        <Typography
                            level="body-xs"
                            sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                color: "primary.plainColor",
                                textTransform: "uppercase",
                            }}
                        >
                            Execution & Materials
                        </Typography>
                        <Grid container spacing={2} mb={3}>
                            <MilestoneField
                                label="Material Purchase"
                                value={materialPurchaseDate}
                                onChange={setMaterialPurchaseDate}
                            />
                            <MilestoneField label="Job Start Date" value={jobStartDate} onChange={setJobStartDate} />
                        </Grid>

                        <Typography
                            level="body-xs"
                            sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                color: "primary.plainColor",
                                textTransform: "uppercase",
                            }}
                        >
                            Project Finalization
                        </Typography>
                        <Grid container spacing={2}>
                            <MilestoneField
                                label="Estimated End"
                                value={estimatedEndDate}
                                onChange={setEstimatedEndDate}
                            />
                            <MilestoneField
                                label="Actual Completion"
                                value={completionDate}
                                onChange={setCompletionDate}
                            />
                        </Grid>
                    </Box>
                ) : milestones.length > 0 ? (
                    <SharedTimeline events={milestones} />
                ) : (
                    <EmptyState />
                )}
            </Box>
        </Box>
    );
}

const MilestoneField = ({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
}) => (
    <Grid xs={12} md={6}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
                level="body-xs"
                sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                }}
            >
                {label}
            </Typography>
            <Input
                type="date"
                fullWidth
                value={value ? new Date(value).toISOString().split("T")[0] : ""}
                onChange={(e) => onChange(e.target.value)}
                size="sm"
                variant="outlined"
                sx={{ borderRadius: "4px", boxShadow: "none", bgcolor: "background.surface" }}
            />
        </Box>
    </Grid>
);

const EmptyState = () => (
    <Box py={6} textAlign="center">
        <Typography level="body-md" color="neutral" sx={{ opacity: 0.6, fontWeight: 500 }}>
            No project milestones recorded yet.
        </Typography>
        <Typography level="body-xs" sx={{ mt: 1 }}>
            Click &quot;Manage Dates&quot; above to start tracking project phases.
        </Typography>
    </Box>
);
